import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import store from './redux/store';

import Gdpr from './Gdpr';

import GlobalStyle from './global_styles';
import defaultStyles from './default_styles';
import consent from './utilities/consent_string/index';

import { boundSetIndexUrls } from './redux/actions/set_index_urls';
import { boundShowGdpr, boundHideGdpr } from './redux/actions/display';
import {
    boundSetCookieName,
    boundSetExpireTimeAllPositive,
    boundSetExpireTimeOneNegative, 
} from './redux/actions/consent';

import { boundSetListUrl, boundFetchList } from './redux/actions/vendor_list';
import bodyScrollHandler from './utilities/handlers/body_scroll';
import isInitalHandler from './utilities/handlers/is_initial_handler';
import { cookiesEnabled } from './utilities/cookies';

import { 
    boundSetCustomPurposesListURL, 
    boundFetchCustomPurposesList 
} from './redux/actions/custom_purposes_list';

import { boundSetLanguage, boundSetDefaultLanguage } from './redux/actions/translated_list';
import languageHandler from './utilities/handlers/language';

(function (window) {
    window.sGdpr = window.sGdpr || {};
    const sGdpr = window.sGdpr;

    let _styles = defaultStyles;
    let _container = null;

    const _createContainer = () => {
        _container = document.createElement('div');
        _container.id = 's-gdpr';
        document.body.appendChild(_container);
    };

    const _bootstraspReact = () => {
        ReactDOM.render(
            <Provider store={store} >
                <GlobalStyle />
                <ThemeProvider theme={_styles}>
                    <Gdpr />
                </ThemeProvider>
            </Provider>, document.getElementById('s-gdpr'));
    };

    const showOrHide = () => {
        if (!store.getState().visible) {
            boundShowGdpr();
        } else {
            boundHideGdpr();
        }
    };

    const _setToStore = (settings) => {

        if (settings.indexUrls) {
            boundSetIndexUrls(settings.indexUrls);
        }

        if (settings.customPurposesListURL) {
            boundSetCustomPurposesListURL(settings.customPurposesListURL);
        }

        if (settings.vendorListURL) {
            boundSetListUrl(settings.vendorListURL);
        }

        if (settings.cookieName) {
            boundSetCookieName(settings.cookieName);
        }

        if (settings.expireTimeAllPositive) {
            boundSetExpireTimeAllPositive(settings.expireTimeAllPositive);
        }

        if (settings.expireTimeOneNegative) {
            boundSetExpireTimeOneNegative(settings.expireTimeOneNegative);
        }

        if (settings.language) {
            boundSetLanguage(settings.language);
        }

        return Promise.all([boundFetchList(), boundFetchCustomPurposesList()]);
    };

    const setCookieName = (name) => boundSetCookieName(name);

    const setExpireTimeAllPositive = (time) => boundSetExpireTimeAllPositive(time);

    const setExpireTimeOneNegative = (time) => boundSetExpireTimeOneNegative(time);

    const setVendorListUrl = (vendorListURL) => {
        boundSetListUrl(vendorListURL);
        boundFetchList()
            .then(() => {
                // clear user consent records
                // we asume that new list has different purposes so the user should choose again --> pop the gdpr
            });
    };

    const getConsent = () => store.getState().consent;
    const isPurposeAllowed = (id) => store.getState().consent.purposes.indexOf(id) !== -1
    

    const setLanguage = (language) => boundSetLanguage(language);

    const _init = (settings = {}) => {
        // if cookies are disabled most likely LS is disabled aswell (chrome) so the app is terminated
        if (!cookiesEnabled()) { return }

        if (settings.styles) {
            _styles = Object.assign(_styles, settings.styles);
        }

        if (settings.callbacks) {
            store.subscribe(isInitalHandler.bind(this, settings.callbacks));
        }

        store.subscribe(bodyScrollHandler);
        store.subscribe(languageHandler);

        _setToStore(settings)
            .then(() => {
                consent.init();
                _createContainer();
                _bootstraspReact();
            })
            .catch((e) => {
                console.error(e);
            });
    };

    sGdpr._init = _init;
    sGdpr.showOrHide = showOrHide;
    sGdpr.show = boundShowGdpr;
    sGdpr.hide = boundHideGdpr;
    sGdpr.setCookieName = setCookieName;
    sGdpr.setExpireTimeAllPositive = setExpireTimeAllPositive;
    sGdpr.setExpireTimeOneNegative = setExpireTimeOneNegative;
    sGdpr.getConsent = getConsent;
    sGdpr.isPurposeAllowed = isPurposeAllowed;
    sGdpr.setVendorListUrl = setVendorListUrl;
    sGdpr.setLanguage = setLanguage;
    sGdpr.setDefaultLanguage = boundSetDefaultLanguage;

})(window);

// this will be stripped by the CRA compiler on build
if (process.env.NODE_ENV !== 'production') {
    window.store = store;
    window.addEventListener('DOMContentLoaded', () => {
        window.sGdpr._init({
            indexUrls: ['onclick=alert(8007)', '#4', '34234', '@3334'],
            vendorListURL: 'http://deelay.me/1000/https://lib.wtg-ads.com/gdpr/vendorlist.json',
            language: 'bg',
            customPurposesListURL: 'https://gdpr.sportal.bg/custom_purposes.json',
            expireTimeAllPositive: 20000, // miliseconds
            expireTimeOneNegative: 10000, // miliseconds
            styles: {
                container: {
                    backgroundColor: '#f1f1f1',
                },
                header: {
                    color: '#fff',
                    backgroundColor: '#1A62B7',
                },
                nav: {
                    borderColor: '#fff',
                    color: '#fff',
                    activeColor: '#fff',
                    backgroundColor: '#CB1919'
                },
                content: {
                    color: 'rgba(0, 0, 0, 0.7)',
                    borderColor: '#0056b3',
                    hoverColor: '#0056b3',
                },
                footer: {
                    color: 'lightblue',
                    // backgroundColor: 'silver',
                    btnColor: '#1A62B7',
                    activeBtnColor: '#fff',
                }
            },
            callbacks: [() => {
                if (window.sGdpr.isPurposeAllowed(2) && window.sGdpr.isPurposeAllowed(3)) {
                    console.log('Purposes 2 and 3 are in the user consent.')
                }
                console.log('custom function executed')
            }],
        });
    });
}